import type { ConnectorCapGeometry, ConnectorGeometry } from "@refinement-canvas/domain";
import {
  connectorPathData,
  connectorPointsAttribute,
  type ConnectorPointMapper,
} from "../../../lib/connector-geometry";

// 接続矢印の線本体と両端の形状を SVG で描く部品。キャンバス上のコネクタと、作成中のプレビューが
// 同じ共有の幾何結果（computeConnectorGeometry）からこの部品で描く（描画面ごとに形を作り直さない）。

interface ConnectorShapeProps {
  readonly geometry: ConnectorGeometry;
  /** 線・形状の色。 */
  readonly color: string;
  /** 描画する線幅（選択中は実際の線幅より太く描くため、幾何計算の線幅と分けて受け取る）。 */
  readonly strokeWidth: number;
  /** 座標を SVG のローカル座標へ写す関数。 */
  readonly map: ConnectorPointMapper;
}

/** 線本体（破線を含む）と両端の形状。当たり判定は持たない（pointer-events: none）。 */
export function ConnectorShape({ geometry, color, strokeWidth, map }: ConnectorShapeProps): JSX.Element {
  const dashed = geometry.dashArray !== null;
  return (
    <g pointerEvents="none">
      <path
        data-testid="connector-body"
        d={connectorPathData(geometry.path, map)}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        // 破線は線幅基準の間隔（4w 3w）をそのまま見せるため線端を丸めない。
        strokeLinecap={dashed ? "butt" : "round"}
        strokeLinejoin="round"
        strokeDasharray={geometry.dashArray?.join(" ")}
      />
      {geometry.startCap ? (
        <ConnectorCapShape
          cap={geometry.startCap}
          end="start"
          color={color}
          strokeWidth={strokeWidth}
          map={map}
        />
      ) : null}
      {geometry.endCap ? (
        <ConnectorCapShape
          cap={geometry.endCap}
          end="end"
          color={color}
          strokeWidth={strokeWidth}
          map={map}
        />
      ) : null}
    </g>
  );
}

/** 端の形状 1 つ。塗りは線と同じ色で塗って同じ太さで縁取り、中空の丸は内側を塗らない。 */
function ConnectorCapShape({
  cap,
  end,
  color,
  strokeWidth,
  map,
}: {
  readonly cap: ConnectorCapGeometry;
  readonly end: "start" | "end";
  readonly color: string;
  readonly strokeWidth: number;
  readonly map: ConnectorPointMapper;
}): JSX.Element {
  // 丸の半径は写像の拡大率に合わせる（キャンバス上は等倍、プレビューも等倍）。
  const origin = map({ x: 0, y: 0 });
  const unit = map({ x: 1, y: 0 });
  const scale = Math.hypot(unit.x - origin.x, unit.y - origin.y) || 1;
  return (
    <g data-testid={`connector-cap-${end}`} data-cap={cap.cap}>
      {cap.fills.map((polygon, index) => (
        <polygon
          key={`fill-${index}`}
          points={connectorPointsAttribute(polygon, map)}
          fill={color}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />
      ))}
      {cap.strokes.map((polyline, index) => (
        <polyline
          key={`stroke-${index}`}
          points={connectorPointsAttribute(polyline, map)}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {cap.circles.map((circle, index) => {
        const center = map(circle.center);
        return (
          <circle
            key={`circle-${index}`}
            cx={center.x}
            cy={center.y}
            r={circle.radius * scale}
            fill={circle.filled ? color : "none"}
            stroke={color}
            strokeWidth={strokeWidth}
          />
        );
      })}
    </g>
  );
}
